/**
 * graph.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { GraphNode, NodeIdentifier } from "./node";
export { GraphNode } from "./node";
import { GraphEdge } from "./edge";
export { GraphEdge, SecondaryEdge } from "./edge";

export type StandardGraph = Graph<GraphNode, GraphEdge>;
export class Graph<N extends GraphNode, E extends GraphEdge>
{
  /**
   * Whether the graph is directed or undirected.
   */
  public directed : boolean;

  public allowsSelfEdges : boolean;

  public readonly categorizeEdges : boolean;

  public knownNodeCategories : string[] = [];

  // CONFIGS
  public readonly idealLinkLength : number;
  public readonly linkStrength : number;
  public readonly chargeStrength : number;

  public reinitialize : boolean = false;

  public byId : Map<NodeIdentifier, N> =
    new Map<NodeIdentifier, N>();

  private addNodeCategory (node : N) : void
  {
    if (node.category && this.knownNodeCategories.indexOf(node.category) === -1)
    {
      this.knownNodeCategories.push(node.category);
    }
  }

  constructor (public nodes : N[] = [], public edges : E[] = [])
  {
    nodes.forEach((node : N) => {
      this.byId.set(node.id, node);
      this.addNodeCategory(node);
    });
  }

  public addNode (node : N) : void
  {
    this.nodes.push(node);
    this.byId.set(node.id, node);
    this.addNodeCategory(node);
  }

  public addEdgeByIds (
    sourceId : NodeIdentifier,
    targetId : NodeIdentifier,
    builder : (a : N, b : N) => E | null
  ) : E
  {
    if (sourceId === targetId && !this.allowsSelfEdges)
    {
      console.error(sourceId, targetId);
      throw new Error("This graph does not support self-edges!");
    }
    const source : N = this.byId.get(sourceId);
    const target : N = this.byId.get(targetId);
    if (source === undefined || target === undefined)
    {
      console.error(sourceId, targetId);
      throw new Error("Could not find all nodes for edge!");
    }

    const edge : E = builder(source, target);
    if (edge)
    {
      // builder can choose to return nothing
      this.edges.push(edge);
    }
    return edge;
  }

  public static sourcedAt (edge : GraphEdge, node : GraphNode) : boolean
  {
    return edge.source === node;
  }
  public static targets (edge : GraphEdge, node : GraphNode) : boolean
  {
    return edge.target === node;
  }

  public static topologicalCompatator (edges : GraphEdge[])
    : (a : GraphNode, b : GraphNode) => number
  {
    return (a : GraphNode, b : GraphNode) => {
      let relative : number = 0;
      for (const edge of edges)
      {
        if (Graph.sourcedAt(edge, a))
        {
          if (Graph.targets(edge, b))
          {
            return -1000;
          }
          relative--;
        }
        if (Graph.sourcedAt(edge, b))
        {
          if (Graph.targets(edge, a))
          {
            return 1000;
          }
          relative++;
        }
      }
      return relative;
    };
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/graphs/graph.ts